import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { withRefLinks } from '../bible/Dictionary';
import { CALLING, lessonKey, STAGES, type Lesson, type Stage } from './content';

type Progress = { lesson: string; doneAt: string | null; note: string | null };

function useProgress() {
  const data = useAsync((signal) => api.get<{ items: Progress[] }>('/devotional/me/calling', signal).then((r) => r.items), []);
  const map = new Map((data.status === 'ready' ? data.data : []).map((p) => [p.lesson, p]));
  const done = (stage: string, lesson: string) => Boolean(map.get(lessonKey(stage, lesson))?.doneAt);
  return { data, map, done };
}

const stageDone = (s: Stage, done: (stage: string, lesson: string) => boolean) => s.lessons.filter((l) => done(s.key, l.key)).length;

/** «Quiero conocerle»: la línea de progreso del creyente, de la salvación a la vida eterna. */
export function CallingPage() {
  const { data, done } = useProgress();
  const total = STAGES.reduce((n, s) => n + s.lessons.length, 0);
  const finished = STAGES.reduce((n, s) => n + stageDone(s, done), 0);
  // La etapa sugerida: la primera que no está completa.
  const current = STAGES.find((s) => stageDone(s, done) < s.lessons.length) ?? STAGES[STAGES.length - 1]!;
  const next = current.lessons.find((l) => !done(current.key, l.key));

  return (
    <div className="page calling">
      <header className="page__header calling__hero">
        <h1>{CALLING.title}</h1>
        <p className="calling__slogan">«{CALLING.slogan}»</p>
        <blockquote className="calling__verse">
          {CALLING.sloganText} <cite>{withRefLinks(CALLING.sloganRef)}</cite>
        </blockquote>
        <p className="muted">{CALLING.intro}</p>
      </header>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? (
        <>
          <Card>
            <p>
              <strong>
                Tu avance: {finished} de {total} lecciones
              </strong>
            </p>
            <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={finished} aria-label="Avance total">
              <span style={{ width: `${Math.round((finished / total) * 100)}%` }} />
            </div>
            {next ? (
              <>
                <Link className="btn btn--block calling__continue" to={`/app/llamado/${current.key}/${next.key}`}>
                  <span>
                    Continuar: Etapa {STAGES.indexOf(current) + 1} · Lección {current.lessons.indexOf(next) + 1}
                  </span>
                </Link>
                <p className="small muted">
                  {current.title} · {next.title}
                </p>
              </>
            ) : (
              <p>
                <strong>¡Completaste todo el camino!</strong> Puedes volver a cualquier lección cuando quieras.
              </p>
            )}
          </Card>
          <ol className="calling-line" aria-label="Línea de progreso">
            {STAGES.map((s, i) => {
              const n = stageDone(s, done);
              const state = n === s.lessons.length ? 'done' : s === current ? 'current' : n > 0 ? 'started' : 'todo';
              return (
                <li key={s.key} className={`calling-line__step calling-line__step--${state}`}>
                  <Link to={`/app/llamado/${s.key}`} className="calling-line__link">
                    <span className="calling-line__dot" aria-hidden="true">
                      {state === 'done' ? '✓' : i + 1}
                    </span>
                    <span className="calling-line__text">
                      <strong>{s.title}</strong>
                      <span className="small muted">{s.subtitle}</span>
                      <span className="small">
                        {n} de {s.lessons.length} lecciones
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
          <p className="small muted">Tu avance y tus notas son privados: solo tú los ves.</p>
        </>
      ) : null}
    </div>
  );
}

/** Una etapa: su versículo, introducción y lecciones. */
export function CallingStagePage() {
  const { stageKey = '' } = useParams();
  const stage = STAGES.find((s) => s.key === stageKey);
  const { data, done } = useProgress();
  if (!stage) return <EmptyState title="Esta etapa no existe." />;
  const i = STAGES.indexOf(stage);
  return (
    <div className="page calling">
      <p>
        <Link to="/app/llamado">← {CALLING.title}</Link>
      </p>
      <header className="page__header">
        <p className="small muted">
          Etapa {i + 1} de {STAGES.length}
        </p>
        <h1>{stage.title}</h1>
        <p className="muted">{stage.subtitle}</p>
      </header>
      <p>{withRefLinks(stage.intro)}</p>
      <p>
        Versículo clave: <strong>{withRefLinks(stage.verse)}</strong>
      </p>
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      <ol className="calling-lessons">
        {stage.lessons.map((l, n) => (
          <li key={l.key} className={done(stage.key, l.key) ? 'calling-lessons__item calling-lessons__item--done' : 'calling-lessons__item'}>
            <Link to={`/app/llamado/${stage.key}/${l.key}`}>
              <span className="calling-lessons__num" aria-hidden="true">
                {done(stage.key, l.key) ? '✓' : n + 1}
              </span>
              <span>
                <strong>{l.title}</strong>
                <span className="small muted"> — {l.summary}</span>
                {done(stage.key, l.key) ? <span className="visually-hidden"> (terminada)</span> : null}
              </span>
            </Link>
          </li>
        ))}
      </ol>
      <StageNav i={i} />
    </div>
  );
}

function StageNav({ i }: { i: number }) {
  const prev = STAGES[i - 1];
  const next = STAGES[i + 1];
  return (
    <div className="actions">
      {prev ? (
        <Link className="btn btn--ghost" to={`/app/llamado/${prev.key}`}>
          ← {prev.title}
        </Link>
      ) : null}
      {next ? (
        <Link className="btn btn--secondary" to={`/app/llamado/${next.key}`}>
          {next.title} →
        </Link>
      ) : null}
    </div>
  );
}

/** Una lección: enseñanza, versículos, preguntas, oración, preguntas frecuentes y nota privada. */
export function CallingLessonPage() {
  const { stageKey = '', lessonKey: lk = '' } = useParams();
  const stage = STAGES.find((s) => s.key === stageKey);
  const lesson = stage?.lessons.find((l) => l.key === lk);
  const { data, map } = useProgress();
  if (!stage || !lesson) return <EmptyState title="Esta lección no existe." />;
  const key = lessonKey(stage.key, lesson.key);
  return (
    <div className="page calling">
      <p>
        <Link to={`/app/llamado/${stage.key}`}>← {stage.title}</Link>
      </p>
      <header className="page__header">
        <p className="small muted">{stage.title}</p>
        <h1>{lesson.title}</h1>
        <p className="muted">{lesson.summary}</p>
      </header>
      <LessonBody lesson={lesson} />
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? <LessonProgress key={key} stage={stage} lesson={lesson} saved={map.get(key) ?? null} onSaved={data.reload} /> : null}
    </div>
  );
}

function LessonBody({ lesson: l }: { lesson: Lesson }) {
  return (
    <>
      <div className="calling__body">
        {l.body.map((p, i) => (
          <p key={i}>{withRefLinks(p)}</p>
        ))}
      </div>
      {l.faq ? (
        <div className="calling-faq">
          {l.faq.map((f) => (
            <details key={f.q} className="calling-faq__item">
              <summary>{f.q}</summary>
              {f.a.map((p, i) => (
                <p key={i}>{withRefLinks(p)}</p>
              ))}
            </details>
          ))}
        </div>
      ) : null}
      <Card title="Para leer">
        <ul className="calling-verses">
          {l.verses.map((v) => (
            <li key={v}>{withRefLinks(v)}</li>
          ))}
        </ul>
      </Card>
      <Card title="Para pensar o conversar">
        <ul>
          {l.questions.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
      </Card>
      {l.prayer ? (
        <Card title="Una oración">
          <p className="calling__prayer">{l.prayer}</p>
        </Card>
      ) : null}
      {l.action ? (
        <p>
          <Link className="btn btn--secondary" to={l.action.to}>
            {l.action.label}
          </Link>
        </p>
      ) : null}
    </>
  );
}

function LessonProgress({ stage, lesson, saved, onSaved }: { stage: Stage; lesson: Lesson; saved: Progress | null; onSaved: () => void }) {
  const [note, setNote] = useState(saved?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  useEffect(() => setNote(saved?.note ?? ''), [saved?.note]);
  const url = `/devotional/me/calling/${stage.key}/${lesson.key}`;
  const done = Boolean(saved?.doneAt);
  const idx = stage.lessons.indexOf(lesson);
  const nextLesson = stage.lessons[idx + 1];
  const nextStage = STAGES[STAGES.indexOf(stage) + 1];

  async function save(body: { done?: boolean; note?: string | null }, text: string) {
    setBusy(true);
    setMsg(null);
    try {
      await api.put(url, body);
      setMsg({ tone: 'success', text });
      onSaved();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card title="Mi nota (privada)">
        <label className="visually-hidden" htmlFor="calling-note">
          Mi nota
        </label>
        <textarea id="calling-note" rows={4} maxLength={4000} value={note} placeholder="Escribe lo que Dios te habló, tus respuestas o tus dudas…" onChange={(e) => setNote(e.target.value)} />
        <div className="actions">
          <Button variant="secondary" busy={busy} disabled={note.trim() === (saved?.note ?? '')} onClick={() => void save({ note: note.trim() || null }, 'Nota guardada.')}>
            Guardar nota
          </Button>
        </div>
      </Card>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <div className="actions">
        <Button variant={done ? 'ghost' : 'primary'} busy={busy} onClick={() => void save({ done: !done }, done ? 'Lección marcada como pendiente.' : '¡Lección terminada!')}>
          {done ? 'Marcar como pendiente' : 'Terminé esta lección'}
        </Button>
        {nextLesson ? (
          <Link className="btn btn--secondary" to={`/app/llamado/${stage.key}/${nextLesson.key}`}>
            Siguiente: {nextLesson.title} →
          </Link>
        ) : nextStage ? (
          <Link className="btn btn--secondary" to={`/app/llamado/${nextStage.key}`}>
            Siguiente etapa: {nextStage.title} →
          </Link>
        ) : (
          <Link className="btn btn--secondary" to="/app/llamado">
            Volver al camino
          </Link>
        )}
      </div>
    </>
  );
}
